import React, {useEffect, useState} from 'react';
import {endOfDay, startOfDay} from 'date-fns';

import {BooleanValue, Column, DatePicker, Loader, Pager, Select, Table} from '@smartplatform/ui';
import {CreateNew} from '../../components';
import store from '../../store';

const { PER_PAGE } = store.ui;
const statuses = [
	{ label: 'Все', value: null },
	{ label: 'Опубликовано', value: true },
	{ label: 'Не опубликовано', value: false },
];

const renderTags = record => record.tags().map(tag => tag.name).join(', ');

const ArticleList = ({ rootPath, type, tabName, path, limit = PER_PAGE }) => {
	const [reload, setReload] = useState();
	const [records, setRecords] = useState();
	const [users, setUsers] = useState();
	const [filter, setFilter] = useState({
		name: '',
		publishAt: null,
		publish: null,
		page: 1,
		ownerId: null,
	});
	
	const init = async () => {
		const where = {
			and: [
				{ type: type.id },
			],
		};
		
		if (filter.publish !== null) {
			where.and.push(
				filter.publish ?
					{ publish: true }
					:
					{
						or: [
							{ publish: null },
							{ publish: false },
						]
					}
			);
		}
		
		if (filter.name) {
			const trimmed = filter.name.trim();
			where.and.push({ name: { ilike: `%${trimmed}%` }});
		}

		if (filter.publishAt) {
			const date = new Date(filter.publishAt);
			where.and.push({ publishAt: { gte: startOfDay(date) }});
			where.and.push({ publishAt: { lte: endOfDay(date) }});
		}
		
		if (filter.ownerId) {
			where.and.push({ ownerId: filter.ownerId });
		}
		
		console.log('where', JSON.stringify(where) );
		
		const data = await store.model.Article.find({
			where,
			fields: ['id', 'name', 'thumbnail', 'imported', 'mainPage', 'image', 'publish', 'publishAt', 'ownerId'],
			include: [
				'tags',
				{
					// включаем в запрос реляцию owner, чтобы она сразу приезжала с сервера внутри записей Article
					relation: 'owner',
					scope: {
						// это оптимизация, включаем только те поля реляции owner (модель User),
						// которые нам нужны для вывода информации, исключая все другие.
						// исключение - поле id, оно обязательно должно быть включено сюда
						fields: ['id', 'firstName', 'lastName', 'middleName', 'username']
					}
				}
			],
			limit,
			skip: (filter.page - 1) * limit,
			order: 'publishAt DESC',
		});
		
		const users = await store.model.User.find({
			fields: ['id', 'firstName', 'lastName', 'middleName', 'username', 'deputyId']
		});
		
		setUsers(users)
		setRecords(data);
	};
	
	const setName = e => setFilter({ ...filter, name: e.target.value });
	const setPublishAt = publishAt => setFilter({ ...filter, publishAt });
	const setPublish = publish => setFilter({ ...filter, publish });
	const setPage = page => setFilter({ ...filter, page });
	const setAuthor = (author) => setFilter({ ...filter, ownerId: author });

	useEffect(() => {
		tabName && store.ui.setPage(tabName);
	}, []);

	useEffect(() => {
		init();
	}, [filter]);

	const getImage = article => {
		// console.log('article', article.imported, article.thumbnail, article.name);
		if (!article.imported && !article.thumbnail) return <span className="empty" />;
		const url = article.imported
			? `/api/containers/articles/download/${article.image}`
			: article.downloadFile('thumbnail');
		return <img src={url + '?image=[{"resize":{"width":120,"height":120}}]'} width={60} height={60} alt="" />;
	};

	const toggleShowOnMain = async record => {
		const articlesOnMain = records.filter(e => e.mainPage === true);

		if (record.mainPage || articlesOnMain.length < store.ui.MAXIMUM_ARTICLES_ON_MAINPAGE) {
			record.mainPage = !record.mainPage;
			await record.save();
			setReload(!reload);
		} else {
			console.info('Too many articles on the Main Page');
		}
	};
	
	const renderOwner = record => {
		const owner = record.owner;
		if (!owner) {
		  return ``;
		}
		const fio = [owner.lastName, owner.firstName, owner.middleName].join(' ').trim();
		return fio || owner.username;
	};
	
	const getUsersList = (id = null) => {
		if (id) {
			const userById = users.find((user) => id === user.id);
			return [userById.lastName, userById.firstName, userById.middleName].join(' ').trim() || userById.username;
		}
		return users
			.filter((u) => u.username && !u.deputyId)
			.map((user) => {
				return {
					label: [user.lastName, user.firstName, user.middleName].join(' ').trim() || user.username,
					value: user.id
				}
			})
	};

	if (!records) {
		return <Loader />;
	}

	return <>
		<div style={{ marginBottom: '15px' }}>
			<CreateNew link={`${path}/new`} name={`Новая запись`} />
		</div>
		
		<div className="row">
			<div className="col-lg-2 col-md-2 col-sm-3 col-12">
				<DatePicker value={filter.publishAt} onChange={setPublishAt} placeholder="Дата публикации" />
			</div>
			<div className="col-lg-2 col-md-2 col-sm-3 col-12">
				<Select items={statuses} value={filter.publish} onChange={setPublish} className="drop-down-md" isRequired />
			</div>
			<div className="col-lg-4 col-md-4 col-sm-3 col-6">
				<input value={filter.name} placeholder="Поиск по имени" onChange={setName} style={{ width: '100%' }} />
			</div>
			
			<div className="col-lg-4 col-md-4 col-sm-3 col-6">
			
				<Select items={getUsersList()} onChange={setAuthor} showValue={filter.ownerId ? getUsersList(filter.ownerId) : <span className='author-placeholder'>Поиск по автору</span> } className="drop-down-md" />
			</div>
		</div>
		
		<Pager
			itemsPerPage={limit}
			totalCount={records.totalCount}
			current={filter.page}
			onChange={setPage}
			directChange={true}
		/>

		<Table
			records={records}
			rootPath={rootPath}
			path={path}
			className="multiline"
			onRowClick={record => store.history.push(`${path}/${record.id}`)}>
			<Column width={60} computed={getImage} label="Миниатюра" className="image-preview" />
			<Column property="name" label="Название" />
			<Column computed={renderTags} label="Категории" />
			<Column width={90} property="publish" label="Опубликовано"><BooleanValue falsyValues={[ null ]} /></Column>
			<Column property="publishAt" label="Дата публикации" />
			<Column computed={renderOwner} label="Автор новости" />
			<Column property="imported" label="Импортировано" />
			<Column property="mainPage" onClick={toggleShowOnMain}><BooleanValue /></Column>
		</Table>
	</>;
};

export default ArticleList;
